import { CloseOutlined, LeftOutlined } from "@ant-design/icons";
import { Button, Flex, Form, InputNumber, Tooltip, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdvancedTable, {
  AddButton,
  AdvancedTableRef,
  EditableColumnType,
  getActionColumns,
} from "../components/AdvancedTable";
import { useKeyValueData } from "../contexts/KeyValueDataProvider";
import {
  PropRarityWeightDataProvider,
  usePropRarityWeightData,
} from "../contexts/PropRarityWeightDataProvider";
import { PropRarityWeight } from "../shared/types/PropRarityWeight";
import { dataCall } from "../tsrpc";

const { Title } = Typography;

export default function PagePropRarityWight({
  backText = "返回道具列表",
  onBack,
  closeButton,
}: {
  backText?: string;
  onBack?: () => void;
  closeButton?: boolean;
} = {}) {
  return (
    <PropRarityWeightDataProvider>
      <InnerComponent backText={backText} onBack={onBack} closeButton={closeButton} />
    </PropRarityWeightDataProvider>
  );
}

function InnerComponent({
  backText,
  onBack,
  closeButton,
}: {
  backText: string;
  onBack?: () => void;
  closeButton?: boolean;
}) {
  const navigate = useNavigate();
  const tableRef = useRef<AdvancedTableRef<PropRarityWeight>>(null);
  const context = usePropRarityWeightData();
  const [editingData, setEditingData] = useState<
    PropRarityWeight | undefined
  >();
  const { data: rarities, load: loadRarities } =
    useKeyValueData("proprarities");
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!rarities || rarities.length === 0) loadRarities();
    getVersion();
  }, []);

  const getVersion = async () => {
    const res = await dataCall("editor/DataFind", {
      collection: "versions",
      filter: { collection: "proprarityweights" },
      limit: 1,
    });
    setVersion(res.res?.data?.[0]?.version ?? 0);
  };

  const TableTitle = () => {
    return (
      <Flex justify="space-between" align="center">
        <Flex gap={10}>
          <Title level={5}>道具品质权重管理</Title>
          <p>{`[数据版本：${version}]`}</p>
        </Flex>
        <Flex gap={10}>
          <AddButton<PropRarityWeight>
            title="新增"
            add={() => context.add}
            createValue={() => {
              let turns = 1;
              while (context.data.some((d) => d.turns === turns)) turns++;
              const res: Omit<PropRarityWeight, "_id"> = { turns };
              rarities.forEach((r) => {
                res[r.value] = 0;
              });
              return res;
            }}
          >
            <Form.Item
              name="turns"
              label="轮次"
              rules={[{ required: true, message: "请输入轮次！" }]}
            >
              <InputNumber min={1} max={100} precision={0} />
            </Form.Item>
            <>
              {rarities.map((r, i) => (
                <Form.Item key={i} name={r.value} label={r.label}>
                  <InputNumber min={0} max={1000} />
                </Form.Item>
              ))}
            </>
          </AddButton>
          {closeButton ? (
            <Tooltip title="关闭">
              <Button
                icon={<CloseOutlined />}
                onClick={onBack ? onBack : () => navigate("/control/props")}
              />
            </Tooltip>
          ) : (
            <Button
              icon={<LeftOutlined />}
              onClick={onBack ? onBack : () => navigate("/control/props")}
            >
              {backText}
            </Button>
          )}
        </Flex>
      </Flex>
    );
  };

  const columns: EditableColumnType<PropRarityWeight>[] = [
    { title: "轮次", dataIndex: "turns", width: 200 },
    ...rarities.map((r) => ({
      title: r.label,
      dataIndex: r.value,
      editor: <InputNumber min={0} max={1000} />,
    })),
    getActionColumns({
      editingData,
      setEditingData,
      display: (d) => `轮次${d?.turns}权重数据`,
      editSubmit: () => tableRef.current?.editSubmit,
      $delete: () => tableRef.current?.delete,
    }),
  ];

  return (
    <AdvancedTable<PropRarityWeight>
      ref={tableRef}
      editingData={editingData}
      context={context}
      title={() => <TableTitle />}
      columns={columns}
    />
  );
}
